import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchGraphQL } from '../../../../lib/graphql';
import {
  Card,
  CardContent,
  Text,
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Badge,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Alert,
  AlertTitle,
  AlertDescription
} from '@tonic/shared/components/ui';
import { ArrowLeft, Plus, Trash2, Save, Users, AlertCircle } from 'lucide-react';

const GET_CUSTOMER_SEGMENT = `
  query GetCustomerSegment($id: ID!) {
    customerSegment(id: $id) {
      id
      name
      description
      criteria {
        type
        value
      }
      customerCount
      customers {
        id
        firstName
        lastName
        email
        loyaltyTier
        totalOrders
        lifetimeValue
      }
    }
  }
`;

const CREATE_CUSTOMER_SEGMENT = `
  mutation CreateCustomerSegment($input: CreateCustomerSegmentInput!) {
    createCustomerSegment(input: $input) {
      id
      name
    }
  }
`;

const UPDATE_CUSTOMER_SEGMENT = `
  mutation UpdateCustomerSegment($id: ID!, $input: UpdateCustomerSegmentInput!) {
    updateCustomerSegment(id: $id, input: $input) {
      id
      name
    }
  }
`;

const DELETE_CUSTOMER_SEGMENT = `
  mutation DeleteCustomerSegment($id: ID!) {
    deleteCustomerSegment(id: $id)
  }
`;

const LOYALTY_TIERS = ['STANDARD', 'SILVER', 'GOLD', 'VIP'];
const RECOMMERCE_TYPES = ['REPAIR', 'REFILL', 'RECYCLE', 'RESALE'];
const ENGAGEMENT_LEVELS = ['HIGH', 'MEDIUM', 'LOW'];

export function CustomerSegmentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isNew = id === 'new';

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    criteria: []
  });

  const [formErrors, setFormErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const { data, isLoading: loading, error } = useQuery({
    queryKey: ['customerSegment', id],
    queryFn: () => fetchGraphQL(GET_CUSTOMER_SEGMENT, { id }),
    enabled: !isNew
  });

  const createSegmentMutation = useMutation({
    mutationFn: (input) => fetchGraphQL(CREATE_CUSTOMER_SEGMENT, { input }),
    onSuccess: (data) => {
      navigate(`/customers/segments/${data.createCustomerSegment.id}`);
    }
  });

  const updateSegmentMutation = useMutation({
    mutationFn: ({ id, input }) => fetchGraphQL(UPDATE_CUSTOMER_SEGMENT, { id, input }),
    onSuccess: () => {
      queryClient.invalidateQueries(['customerSegment', id]);
    }
  });

  const deleteSegmentMutation = useMutation({
    mutationFn: (id) => fetchGraphQL(DELETE_CUSTOMER_SEGMENT, { id }),
    onSuccess: () => {
      navigate('/customers/segments');
    }
  });

  useEffect(() => {
    if (data?.customerSegment) {
      setFormData({
        name: data.customerSegment.name,
        description: data.customerSegment.description,
        criteria: data.customerSegment.criteria
      });
    }
  }, [data]);

  const validateCriterion = (criterion) => {
    const errors = {};
    if (!criterion.type) {
      errors.type = 'Type is required';
      return errors;
    }

    if (!criterion.value) {
      errors.value = 'Value is required';
      return errors;
    }

    switch (criterion.type) {
      case 'PURCHASE_BEHAVIOR':
        if (isNaN(criterion.value) || Number(criterion.value) <= 0) {
          errors.value = 'Must be a positive number';
        }
        break;
      case 'LOYALTY_TIER':
        if (!LOYALTY_TIERS.includes(criterion.value)) {
          errors.value = 'Invalid loyalty tier';
        }
        break;
      case 'RECOMMERCE_ACTIVITY':
        if (!RECOMMERCE_TYPES.includes(criterion.value)) {
          errors.value = 'Invalid re-commerce activity';
        }
        break;
      case 'ENGAGEMENT':
        if (!ENGAGEMENT_LEVELS.includes(criterion.value)) {
          errors.value = 'Invalid engagement level';
        }
        break;
    }
    return errors;
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    } else if (formData.name.length < 3) {
      errors.name = 'Name must be at least 3 characters';
    }

    if (!formData.description.trim()) {
      errors.description = 'Description is required';
    }

    if (!formData.criteria.length) {
      errors.criteria = 'At least one criterion is required';
    } else {
      formData.criteria.forEach((criterion, index) => {
        const criterionErrors = validateCriterion(criterion);
        if (Object.keys(criterionErrors).length > 0) {
          errors[`criteria_${index}`] = criterionErrors;
        }
      });
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setFormErrors({});

    try {
      if (isNew) {
        await createSegmentMutation.mutateAsync(formData);
      } else {
        await updateSegmentMutation.mutateAsync({ id, input: formData });
      }
    } catch (error) {
      console.error('Error saving segment:', error);
      setFormErrors({
        submit: error.message || 'Failed to save segment'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this segment?')) {
      return;
    }

    setIsDeleting(true);
    try {
      await deleteSegmentMutation.mutateAsync(id);
    } catch (error) {
      console.error('Error deleting segment:', error);
      setFormErrors({
        submit: error.message || 'Failed to delete segment'
      });
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
      </div>
    );
  }

  if (error && !isNew) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>
          Error loading segment: {error.message}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            onClick={() => navigate('/admin/customers/segments')}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <div>
            <Text variant="h1" className="text-2xl font-bold">
              {isNew ? 'Create Segment' : 'Edit Segment'}
            </Text>
            <Text variant="body" className="text-muted-foreground">Define segment criteria and view matching customers</Text>
          </div>
        </div>
        {!isNew && (
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={isDeleting}
            className="gap-2"
          >
            <Trash2 className="h-4 w-4" />
            {isDeleting ? 'Deleting...' : 'Delete Segment'}
          </Button>
        )}
      </div>

      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSave} className="space-y-6">
            <div className="space-y-4">
              <div>
                <Text variant="small" className="font-medium mb-1.5">Name</Text>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  error={formErrors.name}
                />
                {formErrors.name && (
                  <Text variant="small" className="text-red-500 mt-1">{formErrors.name}</Text>
                )}
              </div>

              <div>
                <Text variant="small" className="font-medium mb-1.5">Description</Text>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  error={formErrors.description}
                />
                {formErrors.description && (
                  <Text variant="small" className="text-red-500 mt-1">{formErrors.description}</Text>
                )}
              </div>

              {/* Criteria section */}
              <div>
                <Text variant="h2" className="font-semibold mb-4">Segment Criteria</Text>
                {formData.criteria.map((criterion, index) => (
                  <div key={index} className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <Text variant="small" className="font-medium mb-1.5">Type</Text>
                      <Select
                        value={criterion.type}
                        onValueChange={(value) => {
                          const newCriteria = [...formData.criteria];
                          newCriteria[index] = { ...criterion, type: value };
                          setFormData(prev => ({ ...prev, criteria: newCriteria }));
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="PURCHASE_BEHAVIOR">Purchase Behavior</SelectItem>
                          <SelectItem value="LOYALTY_TIER">Loyalty Tier</SelectItem>
                          <SelectItem value="RECOMMERCE_ACTIVITY">Re-Commerce Activity</SelectItem>
                          <SelectItem value="ENGAGEMENT">Engagement</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Text variant="small" className="font-medium mb-1.5">Value</Text>
                      {criterion.type === 'LOYALTY_TIER' ? (
                        <Select
                          value={criterion.value}
                          onValueChange={(value) => {
                            const newCriteria = [...formData.criteria];
                            newCriteria[index] = { ...criterion, value };
                            setFormData(prev => ({ ...prev, criteria: newCriteria }));
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select tier" />
                          </SelectTrigger>
                          <SelectContent>
                            {LOYALTY_TIERS.map(tier => (
                              <SelectItem key={tier} value={tier}>{tier}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          value={criterion.value}
                          onChange={(e) => {
                            const newCriteria = [...formData.criteria];
                            newCriteria[index] = { ...criterion, value: e.target.value };
                            setFormData(prev => ({ ...prev, criteria: newCriteria }));
                          }}
                        />
                      )}
                    </div>
                  </div>
                ))}

                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setFormData(prev => ({
                      ...prev,
                      criteria: [...prev.criteria, { type: '', value: '' }]
                    }));
                  }}
                  className="gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Add Criterion
                </Button>
              </div>

              {formErrors.submit && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Error</AlertTitle>
                  <AlertDescription>{formErrors.submit}</AlertDescription>
                </Alert>
              )}

              <div className="flex justify-end gap-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/admin/customers/segments')}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSaving}
                  className="gap-2"
                >
                  <Save className="h-4 w-4" />
                  {isSaving ? 'Saving...' : 'Save Segment'}
                </Button>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>

      {!isNew && data?.customerSegment?.customers && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-6">
              <Text variant="h2" className="font-semibold">Matching Customers</Text>
              <Badge variant="secondary" className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                {data.customerSegment.customerCount} customers
              </Badge>
            </div>

            <Table>
              <TableHead>
                <TableRow>
                  <TableHeader>Name</TableHeader>
                  <TableHeader>Email</TableHeader>
                  <TableHeader>Loyalty Tier</TableHeader>
                  <TableHeader>Orders</TableHeader>
                  <TableHeader>Lifetime Value</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.customerSegment.customers.map((customer) => (
                  <TableRow key={customer.id}>
                    <TableCell>
                      {customer.firstName} {customer.lastName}
                    </TableCell>
                    <TableCell>{customer.email}</TableCell>
                    <TableCell>
                      <Badge variant={
                        customer.loyaltyTier === 'VIP' ? 'destructive' :
                          customer.loyaltyTier === 'GOLD' ? 'warning' :
                            customer.loyaltyTier === 'SILVER' ? 'secondary' : 'outline'
                      }>
                        {customer.loyaltyTier}
                      </Badge>
                    </TableCell>
                    <TableCell>{customer.totalOrders}</TableCell>
                    <TableCell>${customer.lifetimeValue.toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
} 
